// WhatsAppConversationsTable.tsx
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Tag,
  Avatar,
  Input,
  Button,
  Badge,
  Modal,
  Row,
  Col,
  Card,
  Typography,
  Space,
  Select,
  message,
  Tooltip,
  Drawer,
  Form,
  Spin,
  Empty,
  Dropdown,
} from "antd";
import {
  MessageOutlined,
  UserOutlined,
  SearchOutlined,
  PhoneOutlined,
  FormOutlined,
  EyeOutlined,
  FilterOutlined,
  ReloadOutlined,
  WhatsAppOutlined,
  CheckCircleOutlined,
  EditOutlined,
  StarFilled,
  PlusOutlined,
  DownOutlined,
  FileAddOutlined,
} from "@ant-design/icons";
import api from "../../../services/api";
import { showBasicAlert } from "../../../core/common/useAlert";
import Datatable from "../../../core/common/dataTable";
import AddLeadFromWhatsAppFormModal from "./AddLeadFromWhatsAppFormModal"; // Import the new component
import { LinkOutlined } from "@mui/icons-material";
import WhatsAppFlowMappingModal from "./WhatsAppFlowMappingModal";

const { Title, Text } = Typography;
const { Option } = Select;

// Type definitions based on API response
interface WhatsAppMessage {
  id: number;
  phone_number_id: string;
  from: string;
  message_id: string;
  type: string;
  text_body: string | null;
  submitted_data: any;
  flow_id: string | null;
  flow_token: string | null;
  action: string | null;
  raw_payload: any;
  company_id: number;
  company_type: string;
  createdAt: string;
  updatedAt: string;
  waba_id: number;
}

interface Conversation {
  id: number;
  customer_name: string;
  phone: string;
  last_message: string;
  last_message_type: string;
  last_message_time: string;
  total_messages: number;
  unread_count: number;
  company_id: number;
  company_type: string;
  messages: WhatsAppMessage[];
  lead_status: string;
  lead_score: number;
  waba_id: number;
  phone_number_id: string;
}

const WhatsAppConversationsTable: React.FC = () => {
  const location = useLocation();
  const signature = localStorage.getItem("signature");
  const [form] = Form.useForm();

  // Get navigation state
  const {
    accountId,
    phoneNumberId,
    businessName,
    displayPhoneNumber,
    companyId,
    companyType,
  } = location.state || {};

  // State variables
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [allMessages, setAllMessages] = useState<WhatsAppMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterLeadStatus, setFilterLeadStatus] = useState("all");
  const [selectedConversation, setSelectedConversation] =
    useState<Conversation | null>(null);
  const [isConversationDrawerVisible, setIsConversationDrawerVisible] =
    useState(false);

  // New state for lead form modal
  const [isAddLeadModalVisible, setIsAddLeadModalVisible] = useState(false);
  const [selectedConversationForLead, setSelectedConversationForLead] =
    useState<Conversation | null>(null);
  const [isFlowMappingModalVisible, setIsFlowMappingModalVisible] =
    useState(false);
  const [
    selectedConversationForFlowMapping,
    setSelectedConversationForFlowMapping,
  ] = useState<Conversation | null>(null);

  // Fetch all messages in a single API call
  const fetchAllMessages = async (): Promise<WhatsAppMessage[]> => {
    try {
      setLoading(true);

      const response = await api.post("leadService/getWhatsappConversation", {
        signature,
        phone_number_id: phoneNumberId,
        company_id: companyId,
        company_type: companyType,
      });

      // Extract data from response
      const messageData = response.data?.message?.[0]?.data;

      if (!messageData) {
        return [];
      }

      const messages = messageData.messages || [];
      setAllMessages(messages);
      return messages;
    } catch (err: any) {
      console.error("Error fetching messages:", err);
      showBasicAlert(
        err?.response?.data?.message?.[0]?.msg || "Error fetching messages"
      );
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Group messages by sender (from field) and create conversations
  const createConversationsFromMessages = (
    messages: WhatsAppMessage[]
  ): Conversation[] => {
    // Group messages by sender phone number
    const groupedBySender: { [key: string]: WhatsAppMessage[] } = {};

    messages.forEach((message) => {
      const sender = message.from;
      if (!groupedBySender[sender]) {
        groupedBySender[sender] = [];
      }
      groupedBySender[sender].push(message);
    });

    // Create conversation objects for each sender
    const conversations: Conversation[] = Object.entries(groupedBySender).map(
      ([senderPhone, senderMessages]) => {
        // Sort messages by createdAt (oldest first)
        const sortedMessages = [...senderMessages].sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );

        const lastMessage = sortedMessages[sortedMessages.length - 1];
        const firstMessage = sortedMessages[0];

        // Extract customer name from raw_payload
        let customerName = `Customer ${senderPhone}`;
        if (
          firstMessage.raw_payload?.entry?.[0]?.changes?.[0]?.value
            ?.contacts?.[0]?.profile?.name
        ) {
          customerName =
            firstMessage.raw_payload.entry[0].changes[0].value.contacts[0]
              .profile.name;
        } else if (firstMessage.raw_payload?.messages?.[0]?.from) {
          // Alternative location for customer info
          customerName = `Customer ${firstMessage.raw_payload.messages[0].from}`;
        }

        // Get metadata from the first message
        const wabaId = firstMessage.waba_id || 0;
        const messagePhoneNumberId = firstMessage.phone_number_id || "";

        // Create last message text
        let lastMessageText = "";
        if (lastMessage.text_body) {
          lastMessageText = lastMessage.text_body;
        } else if (lastMessage.submitted_data) {
          lastMessageText = "Form data submitted";
        } else if (lastMessage.type === "flow") {
          lastMessageText = "Flow completed";
        }

        // Calculate unread count (assuming status 1 is unread)
        const unreadCount = sortedMessages.filter(
          (msg: any) => msg.status === 1
        ).length;

        return {
          id:
            firstMessage.id || Number(senderPhone.replace(/\D/g, "").slice(-6)),
          customer_name: customerName,
          phone: senderPhone,
          last_message: lastMessageText,
          last_message_type: lastMessage.type || "text",
          last_message_time: lastMessage.createdAt || new Date().toISOString(),
          total_messages: sortedMessages.length,
          unread_count: unreadCount,
          company_id: firstMessage.company_id || 0,
          company_type: firstMessage.company_type || "",
          waba_id: wabaId,
          phone_number_id: messagePhoneNumberId,
          messages: sortedMessages,
          lead_status: "new", // Default value
          lead_score: 50, // Default value
        };
      }
    );

    // Sort conversations by last message time (newest first)
    return conversations.sort(
      (a, b) =>
        new Date(b.last_message_time).getTime() -
        new Date(a.last_message_time).getTime()
    );
  };

  // Filter messages for a specific sender from the already fetched data
  const getMessagesForSender = (senderPhone: string): WhatsAppMessage[] => {
    return allMessages.filter((msg) => msg.from === senderPhone);
  };

  // Fetch all conversations (single API call)
  const fetchAllConversations = async () => {
    try {
      setLoading(true);

      if (!phoneNumberId || !companyId) {
        showBasicAlert("Please select a WhatsApp account first");
        return;
      }

      // Fetch all messages in a single API call
      const messages = await fetchAllMessages();

      if (messages.length === 0) {
        setConversations([]);
        return;
      }

      // Create conversations from the fetched messages
      const conversations = createConversationsFromMessages(messages);
      setConversations(conversations);
    } catch (err: any) {
      console.error("Error fetching conversations:", err);
    } finally {
      setLoading(false);
    }
  };

  // Get message type badge
  const getMessageTypeBadge = (type: string) => {
    switch (type) {
      case "text":
        return <Tag color="blue">Text</Tag>;
      case "button":
        return (
          <Tag color="purple">
            <FormOutlined /> Button
          </Tag>
        );
      case "flow":
        return (
          <Tag color="green">
            <FormOutlined /> Flow
          </Tag>
        );
      case "interactive":
        return <Tag color="orange">Interactive</Tag>;
      case "location":
        return <Tag color="geekblue">Location</Tag>;
      case "image":
        return <Tag color="cyan">Image</Tag>;
      case "document":
        return <Tag color="magenta">Document</Tag>;
      default:
        return <Tag>{type}</Tag>;
    }
  };

  // Get lead status badge
  const getLeadStatusBadge = (status: string) => {
    switch (status) {
      case "new":
        return <Tag color="blue">New</Tag>;
      case "contacted":
        return <Tag color="orange">Contacted</Tag>;
      case "qualified":
        return <Tag color="green">Qualified</Tag>;
      case "converted":
        return <Tag color="purple">Converted</Tag>;
      case "closed":
        return <Tag color="red">Closed</Tag>;
      default:
        return <Tag>Unknown</Tag>;
    }
  };

  // Filter conversations
  const filteredConversations = conversations.filter((conv) => {
    const matchesSearch =
      searchText === "" ||
      conv.customer_name.toLowerCase().includes(searchText.toLowerCase()) ||
      conv.phone.includes(searchText) ||
      conv.last_message.toLowerCase().includes(searchText.toLowerCase());

    const matchesType =
      filterType === "all" || conv.last_message_type === filterType;
    const matchesLeadStatus =
      filterLeadStatus === "all" || conv.lead_status === filterLeadStatus;

    return matchesSearch && matchesType && matchesLeadStatus;
  });

  const handleViewConversation = (conversation: Conversation) => {
    const senderMessages = getMessagesForSender(conversation.phone);

    if (senderMessages.length === 0) {
      showBasicAlert("No messages found for this conversation");
      return;
    }

    const sortedMessages = [...senderMessages].sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );

    const lastMessage = sortedMessages[sortedMessages.length - 1];

    const updatedConversation: Conversation = {
      ...conversation,
      messages: sortedMessages,
      total_messages: sortedMessages.length,
      last_message:
        lastMessage.text_body ||
        (lastMessage.submitted_data
          ? "Form data submitted"
          : lastMessage.type === "flow"
          ? "Flow completed"
          : "No message"),
      last_message_type: lastMessage.type || "text",
      last_message_time: lastMessage.createdAt || new Date().toISOString(),
    };

    setSelectedConversation(updatedConversation);
    setIsConversationDrawerVisible(true);
  };

  // Open add lead modal
  const handleAddLeadFromForm = (conversation: Conversation) => {
    setSelectedConversationForLead(conversation);
    setIsAddLeadModalVisible(true);
  };

  // Handle lead addition success
  const handleLeadAddedSuccess = async () => {
    showBasicAlert("Lead added successfully!");
    await fetchAllConversations();
  };

  // Add new handler function
  const handleAutoMapWhatsAppFlow = (conversation: Conversation) => {
    setSelectedConversationForFlowMapping(conversation);
    setIsFlowMappingModalVisible(true);
  };

  // Reset filters
  const handleResetFilters = () => {
    setSearchText("");
    setFilterType("all");
    setFilterLeadStatus("all");
  };

  // Table columns for conversations
  const columns = [
    {
      title: "Customer",
      key: "customer",
      width: 200,
      render: (record: Conversation) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Avatar
            size="small"
            icon={<UserOutlined />}
            style={{
              backgroundColor: record.unread_count > 0 ? "#1890ff" : "#ccc",
              color: "white",
            }}
          />
          <div>
            <div style={{ fontWeight: 500, fontSize: "14px" }}>
              {record.customer_name}
            </div>
            <div style={{ fontSize: "12px", color: "#666" }}>
              <PhoneOutlined style={{ marginRight: 4 }} />
              {record.phone}
            </div>
            <div style={{ fontSize: "11px", color: "#999" }}>
              WABA ID: {record.waba_id}
            </div>
          </div>
          {record.unread_count > 0 && (
            <Badge count={record.unread_count} style={{ marginLeft: 8 }} />
          )}
        </div>
      ),
    },
    {
      title: "Status",
      key: "lead_status",
      width: 120,
      render: (record: Conversation) => (
        <div>{getLeadStatusBadge(record.lead_status)}</div>
      ),
      filters: [
        { text: "New", value: "new" },
        { text: "Contacted", value: "contacted" },
        { text: "Qualified", value: "qualified" },
        { text: "Converted", value: "converted" },
        { text: "Closed", value: "closed" },
      ],
      onFilter: (value: any, record: Conversation) =>
        record.lead_status === value,
    },
    {
      title: "Last Message",
      key: "last_message",
      render: (record: Conversation) => (
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              marginBottom: 4,
            }}>
            {getMessageTypeBadge(record.last_message_type)}
            <span style={{ fontSize: "14px", color: "#333" }}>
              {record.last_message && record.last_message.length > 50
                ? `${record.last_message.substring(0, 50)}...`
                : record.last_message || "No message"}
            </span>
          </div>
          <div style={{ fontSize: "12px", color: "#999" }}>
            {record.last_message_time
              ? new Date(record.last_message_time).toLocaleString()
              : "Unknown"}
          </div>
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 250,
      render: (record: Conversation) => (
        <Space size="small">
          <Tooltip title="View Conversation">
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewConversation(record)}>
              View
            </Button>
          </Tooltip>
          <Tooltip title="Add to Lead Form">
            <Button
              type="default"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => handleAddLeadFromForm(record)}>
              Add to Lead Form Manually
            </Button>
          </Tooltip>
          {/* <Tooltip title="Auto-Map WhatsApp Flow">
            <Button
              type="primary"
              size="small"
              icon={<LinkOutlined />}
              onClick={() => handleAutoMapWhatsAppFlow(record)}
              style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
            >
              Auto-Map Flow
            </Button>
          </Tooltip> */}
        </Space>
      ),
    },
  ];

  // Initial fetch
  useEffect(() => {
    if (phoneNumberId && companyId) {
      fetchAllConversations();
    }
  }, [phoneNumberId, companyId, companyType]);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div style={{ padding: "20px" }}>
          {/* Header with Account Info */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "24px",
            }}>
            <div>
              <Title
                level={2}
                style={{
                  margin: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}>
                <WhatsAppOutlined style={{ color: "#25D366" }} />
                WhatsApp Conversations
                {businessName && (
                  <Tag color="blue" style={{ marginLeft: 8 }}>
                    {businessName}
                  </Tag>
                )}
              </Title>
              <Text type="secondary">
                {phoneNumberId && displayPhoneNumber
                  ? `Phone: ${displayPhoneNumber} • Phone No ID: ${phoneNumberId}`
                  : "Manage WhatsApp conversations"}
              </Text>
              <div style={{ fontSize: "12px", color: "#666", marginTop: 4 }}>
                Conversations grouped by sender phone number
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <Row gutter={16} style={{ marginBottom: "24px" }}>
            <Col span={6}>
              <Card bordered={false} style={{ background: "#e6f7ff" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}>
                  <div>
                    <Title level={3} style={{ margin: 0, color: "#1890ff" }}>
                      {conversations.length}
                    </Title>
                    <Text type="secondary">Total Conversations</Text>
                  </div>
                  <MessageOutlined
                    style={{ fontSize: "32px", color: "#1890ff" }}
                  />
                </div>
              </Card>
            </Col>
            <Col span={6}>
              <Card bordered={false} style={{ background: "#f6ffed" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}>
                  <div>
                    <Title level={3} style={{ margin: 0, color: "#52c41a" }}>
                      {allMessages.length}
                    </Title>
                    <Text type="secondary">Total Messages</Text>
                  </div>
                  <CheckCircleOutlined
                    style={{ fontSize: "32px", color: "#52c41a" }}
                  />
                </div>
              </Card>
            </Col>
          </Row>

          {/* Conversations Table */}
          <Card
            title={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}>
                <span>Conversations ({filteredConversations.length})</span>
                <Text type="secondary" style={{ fontSize: "14px" }}>
                  Showing {filteredConversations.length} of{" "}
                  {conversations.length} conversations
                </Text>
              </div>
            }>
            {loading ? (
              <div style={{ textAlign: "center", padding: "50px" }}>
                <Spin size="large" />
                <p>Loading conversations...</p>
              </div>
            ) : conversations.length === 0 ? (
              <Empty
                image={
                  <WhatsAppOutlined
                    style={{ fontSize: "64px", color: "#ddd" }}
                  />
                }
                imageStyle={{ height: 80 }}
                description={
                  <div>
                    <Title level={4} style={{ color: "#999" }}>
                      No Conversations Found
                    </Title>
                    <Text type="secondary">
                      Start messaging on WhatsApp to see conversations here
                    </Text>
                  </div>
                }
              />
            ) : (
              <Datatable columns={columns} dataSource={filteredConversations} />
            )}
          </Card>
        </div>
      </div>

      {/* Footer */}
      <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
        <p className="mb-0">2025 © CMS.</p>
        <p>
          Designed & Developed By{" "}
          <Link
            to="#"
            onClick={(e: any) => e.preventDefault()}
            className="text-primary">
            ZYVO
          </Link>
        </p>
      </div>

      {/* Conversation Drawer */}
      <Drawer
        title={
          selectedConversation && (
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 8,
                }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}>
                  <Avatar
                    size="large"
                    icon={<UserOutlined />}
                    style={{ backgroundColor: "#1890ff" }}
                  />
                  <div>
                    <Title level={4} style={{ margin: 0 }}>
                      {selectedConversation.customer_name}
                    </Title>
                    <Text type="secondary">
                      <PhoneOutlined style={{ marginRight: 4 }} />
                      {selectedConversation.phone}
                    </Text>
                  </div>
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginTop: 8,
                }}>
                {getLeadStatusBadge(selectedConversation.lead_status)}

                {/* Add button in drawer too */}
                <Button
                  size="small"
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => handleAddLeadFromForm(selectedConversation)}>
                  Add to Lead Form Manually
                </Button>
                {/* <Button
                  size="small"
                  type="primary"
                  icon={<LinkOutlined />}
                  onClick={() =>
                    handleAutoMapWhatsAppFlow(selectedConversation)
                  }
                  style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
                >
                  Auto-Map Flow
                </Button> */}
              </div>
            </div>
          )
        }
        placement="right"
        onClose={() => setIsConversationDrawerVisible(false)}
        open={isConversationDrawerVisible}
        width={800}>
        {selectedConversation && (
          <div>
            {/* Conversation Metadata */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
                <div>
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    Sender Phone:
                  </Text>
                  <div style={{ fontWeight: 500 }}>
                    {selectedConversation.phone}
                  </div>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    Receiver Phone ID:
                  </Text>
                  <div style={{ fontWeight: 500 }}>
                    {selectedConversation.phone_number_id}
                  </div>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    Sender WABA ID:
                  </Text>
                  <div style={{ fontWeight: 500 }}>
                    {selectedConversation.waba_id}
                  </div>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    Total Messages:
                  </Text>
                  <div style={{ fontWeight: 500 }}>
                    {selectedConversation.total_messages}
                  </div>
                </div>
              </div>
            </Card>

            {/* Messages List */}
            <div style={{ maxHeight: "500px", overflowY: "auto" }}>
              {selectedConversation.messages.length === 0 ? (
                <Empty description="No messages found" />
              ) : (
                [...selectedConversation.messages]
                  .sort(
                    (a, b) =>
                      new Date(b.createdAt).getTime() -
                      new Date(a.createdAt).getTime()
                  )
                  .map((message) => (
                    <Card
                      key={message.id}
                      size="small"
                      style={{
                        marginBottom: 16,
                        borderLeft: "4px solid #1890ff",
                      }}>
                      <div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: 8,
                          }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}>
                            {getMessageTypeBadge(message.type)}
                            <Text type="secondary" style={{ fontSize: "12px" }}>
                              {new Date(message.createdAt).toLocaleString()}
                            </Text>
                          </div>
                          <Text type="secondary" style={{ fontSize: "12px" }}>
                            Message ID: {message.message_id}
                          </Text>
                        </div>

                        {message.text_body && (
                          <div style={{ marginBottom: 8 }}>
                            <Text>{message.text_body}</Text>
                          </div>
                        )}

                        {message.type === "button" && message.action && (
                          <div style={{ marginBottom: 8 }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                              }}>
                              <FormOutlined style={{ color: "#722ed1" }} />
                              <Text strong>Action:</Text>
                              <Text>{message.action}</Text>
                            </div>
                          </div>
                        )}

                        {message.submitted_data && (
                          <div style={{ marginBottom: 8 }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                marginBottom: 8,
                              }}>
                              <FormOutlined style={{ color: "#52c41a" }} />
                              <Text strong>Submitted Data:</Text>
                            </div>
                            <pre
                              style={{
                                fontSize: "12px",
                                background: "#f5f5f5",
                                padding: 8,
                                borderRadius: 4,
                                maxHeight: "200px",
                                overflow: "auto",
                              }}>
                              {JSON.stringify(message.submitted_data, null, 2)}
                            </pre>
                          </div>
                        )}

                        {message.flow_id && (
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#666",
                              marginTop: 8,
                            }}>
                            <Text>Flow ID: {message.flow_id}</Text>
                            {message.flow_token && (
                              <Text> • Token: {message.flow_token}</Text>
                            )}
                          </div>
                        )}
                      </div>
                    </Card>
                  ))
              )}
            </div>
          </div>
        )}
      </Drawer>

      <AddLeadFromWhatsAppFormModal
        visible={isAddLeadModalVisible}
        onCancel={() => {
          setIsAddLeadModalVisible(false);
          setSelectedConversationForLead(null);
        }}
        conversation={selectedConversationForLead}
        signature={signature}
        onSuccess={handleLeadAddedSuccess}
      />
      <WhatsAppFlowMappingModal
        visible={isFlowMappingModalVisible}
        onCancel={() => {
          setIsFlowMappingModalVisible(false);
          setSelectedConversationForFlowMapping(null);
        }}
        conversation={selectedConversationForFlowMapping}
        signature={signature}
        onSuccess={() => {
          showBasicAlert("Flow mappings saved successfully!");
          setIsFlowMappingModalVisible(false);
        }}
      />
    </div>
  );
};

export default WhatsAppConversationsTable;
